import { isTruthy } from '@ardoq/common-helpers';
import { TextInput } from '@ardoq/forms';
import { Select } from '@ardoq/select';
import { type I18nLocale, t } from '@ardoq/i18n';
import {
  GetInvalidGroupingSelectOptions,
  ParentGroupingRule,
  PerspectiveGroupingRule,
  PerspectivesGroupsComponentTypes,
  PerspectivesGroupsWorkspace,
  toComponentTypeOption,
  toWorkspaceOption,
} from '@ardoq/perspectives';
import { ArdoqId } from '@ardoq/api-types';
import { RowHeader } from '../../atoms';
import { GroupNameHeaderInfoIcon } from '@ardoq/perspectives';

type ParentGroupRuleProps = {
  groupingRule: ParentGroupingRule;
  workspaces: PerspectivesGroupsWorkspace[];
  componentTypesPerWorkspace: Record<
    ArdoqId,
    PerspectivesGroupsComponentTypes[]
  >;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  tabIndex?: number;
  i18nLocale?: I18nLocale;
};

const ParentGroupRule = ({
  workspaces,
  groupingRule,
  componentTypesPerWorkspace,
  onGroupingRuleUpdated,
  getInvalidGroupingSelectOptions,
  tabIndex,
  i18nLocale = {},
}: ParentGroupRuleProps) => {
  const { workspaceSelectOption, ruleTargetSelectOption } =
    getInvalidGroupingSelectOptions?.(groupingRule) || {};

  const workspaceOptions = [
    workspaceSelectOption,
    ...workspaces.map(workspace => toWorkspaceOption(workspace)),
  ].filter(isTruthy);

  const componentTypes =
    componentTypesPerWorkspace[groupingRule.workspaceId!] ?? [];
  const componentTypeOptions = [
    ruleTargetSelectOption,
    ...componentTypes.map(toComponentTypeOption),
  ].filter(isTruthy);

  const updateGroupRule = (partialGroupingRule: Partial<ParentGroupingRule>) =>
    onGroupingRuleUpdated(groupingRule.id, partialGroupingRule);

  return (
    <>
      <RowHeader>
        {t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.inWorkspace',
          'In workspace'
        )}
      </RowHeader>
      <Select
        dataTestId="parent-grouping-rule-workspace-select"
        value={groupingRule.workspaceId}
        options={workspaceOptions}
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.workspaceAriaLabel',
          'Workspace'
        )}
        tabIndex={tabIndex}
        onChange={event => {
          const nextWorkspaceId = event?.value;
          if (!nextWorkspaceId) return;
          const defaultComponentTypeId =
            componentTypesPerWorkspace[nextWorkspaceId]?.[0]?.id;
          updateGroupRule({
            workspaceId: nextWorkspaceId,
            targetId: defaultComponentTypeId,
          });
        }}
        menuPortalTarget={null}
      />
      <RowHeader>
        {t(i18nLocale, 'perspectivesSidebar:groupingRules.type', 'Type')}
      </RowHeader>
      <Select
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.componentTypesAriaLabel',
          'Component types'
        )}
        dataTestId="parent-grouping-rule-component-type-select"
        options={componentTypeOptions}
        value={groupingRule.targetId}
        tabIndex={tabIndex}
        onChange={event =>
          updateGroupRule({
            targetId: event?.value,
          })
        }
        menuPortalTarget={null}
      />

      <RowHeader>
        {t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.groupName',
          'Group name'
        )}
        <GroupNameHeaderInfoIcon />
      </RowHeader>
      <TextInput
        value={groupingRule.sublabel}
        dataTestId="parent-grouping-rule-sublabel-input"
        tabIndex={tabIndex}
        onValueChange={value =>
          updateGroupRule({
            sublabel: value,
          })
        }
      />
    </>
  );
};

export { ParentGroupRule };
